import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

export type VimeoEmbedHandle = {
    pause: () => void;
};

type VimeoEmbedProps = {
    url: string;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    lazy?: boolean; // optional lazy load iframe
    fill?: boolean;
    controls?: boolean;
    onEnded?: () => void;
};

const VimeoEmbed = forwardRef<VimeoEmbedHandle, VimeoEmbedProps>(({
    url,
    autoplay = false,
    muted = false,
    loop = false,
    lazy = true,
    fill = false,
    controls = true,
    onEnded,
}, ref) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const onEndedRef = useRef(onEnded);

    useEffect(() => {
        onEndedRef.current = onEnded;
    }, [onEnded]);

    useEffect(() => {
        if (!onEnded) return;

        const subscribeToEnded = () => {
            iframeRef.current?.contentWindow?.postMessage({ method: 'addEventListener', value: 'ended' }, '*');
        };
        const handleMessage = (event: MessageEvent) => {
            let isVimeoOrigin = false;
            try {
                const hostname = new URL(event.origin).hostname;
                isVimeoOrigin = hostname === 'vimeo.com' || hostname.endsWith('.vimeo.com');
            } catch {
                return;
            }
            if (event.source !== iframeRef.current?.contentWindow || !isVimeoOrigin) return;

            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }
            }

            if (data?.event === 'ready') subscribeToEnded();
            if (data?.event === 'ended') onEndedRef.current?.();
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onEnded]);

    useImperativeHandle(ref, () => ({
        pause: () => {
            const iframe = iframeRef.current;
            if (!iframe?.contentWindow) return;
            iframe.contentWindow.postMessage({ method: 'pause' }, '*');
        },
    }), []);

    // const finalUrl = useMemo(() => {
    //     const hasParams = url.includes('?');
    //     const baseUrl = url.includes('vimeo.com')
    //         ? url.split('?')[0]
    //         : `https://player.vimeo.com/video/${url}`;

    //     const queryParams = new URLSearchParams();
    //     queryParams.set('autoplay', autoplay ? '1' : '0');
    //     queryParams.set('muted', muted ? '1' : '0');
    //     queryParams.set('loop', loop ? '1' : '0');
    //     queryParams.set('autopause', '0');

    //     return `${baseUrl}${hasParams ? '&' : '?'}${queryParams.toString()}`;
    // }, [url, autoplay, muted, loop]);
    const finalUrl = useMemo(() => {
        if (!url) return '';

        try {
            const raw = String(url).trim();
            if (!raw) return '';

            let base = raw;
            let existing = new URLSearchParams();

            if (raw.includes('vimeo.com')) {
                const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
                base = `${u.origin}${u.pathname}`;
                existing = new URLSearchParams(u.search);
            } else {
                const normalized = raw.replace('/?', '?').replace(/^\/+|\/+$/g, '');
                const [id, qs] = normalized.split('?');
                if (!id) return '';
                base = `https://player.vimeo.com/video/${id}`;
                existing = new URLSearchParams(qs || '');
            }

            const qp = new URLSearchParams();
            const h = existing.get('h');
            if (h) qp.set('h', h);

            qp.set('autoplay', autoplay ? '1' : '0');
            qp.set('muted', muted ? '1' : '0');
            qp.set('loop', loop ? '1' : '0');
            qp.set('controls', controls ? '1' : '0');
            qp.set('autopause', '0');
            if (onEnded) qp.set('api', '1');

            return `${base}?${qp.toString()}`;
        } catch {
            return '';
        }
    }, [url, autoplay, muted, loop, controls, onEnded]);

    if (!finalUrl) return null;
    
    return (
        <div
            style={{
                position: 'relative',
                paddingBottom: fill ? 0 : '56.25%', // 16:9 ratio when rendered inline
                height: fill ? '100%' : 0,
                overflow: 'hidden',
                width: '100%',
                //boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
            }}
        >
            <iframe
                ref={iframeRef}
                src={finalUrl}
                loading={lazy ? 'lazy' : 'eager'}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
                onLoad={() => {
                    if (onEnded) {
                        iframeRef.current?.contentWindow?.postMessage({ method: 'addEventListener', value: 'ended' }, '*');
                    }
                }}
            />
        </div>
    );
});

VimeoEmbed.displayName = 'VimeoEmbed';

export default VimeoEmbed;
